import { Component, Input, HostListener, Directive, ContentChild, TemplateRef, Optional, AfterViewInit, Host, ViewChildren, QueryList, ContentChildren, Inject, PLATFORM_ID } from '@angular/core';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { RouterLinkWithHref } from '@angular/router';
import { take, delay } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import * as e from 'express';



/*
   Inserts a non-link header item into the <aims-side-menu>, if collapsible is set to true, can collapse the items inside it

  Example usage:
    <aims-side-menu-header [displayText]="'Shipping'" [collapsible]="true" >
      <aims-side-menu-link [route]="'./printShippingLabel'"
                           [component]="permissions.ComponentID.PrintShippingLabel"
                           [permission]="menuPerms.create"
                           [iconClass]="'fas fa-tag'">
        Print Shipping Label
      </aims-side-menu-link>
    </aims-side-menu-header>

  [displayText]: required, The text to display in the header, NOTE THE QUOTES AROUND THE TEXT
  [permission]: optional, works same as the <aims-side-link> above
  [component]: optional, works same as the <aims-side-link> above
  [collapsible]: optional, defaults to false, if true enables collapsing of items inside the header when clicked
  [defaultOpen]: optional, defaults to true, if collapsible is set to true, setting to false will start the header as collapsed on init
 */

@Component({
  selector: 'aims-side-menu-header',
  styleUrls: ['./aimsSideMenu.component.css'],
  template: `
<li *ngIf="calcIf()">
    <a *ngIf="!(iconHM|async)" (click)="doClick()" class="sidebarNonLink">
        <b>{{ displayText }}</b> &nbsp; <ng-container *ngIf="collapsible"> <span *ngIf="!showList"><i class="fas fa-chevron-right"></i> </span><span *ngIf="showList"><i class="fas fa-chevron-down"></i></span> </ng-container>
    </a>
    <ul *ngIf="showList" class="nav">
        <ng-content></ng-content>
    </ul>
</li>
`
})
export class AimsSideMenuHeaderComponent {
  constructor(private permissions: PermissionsService) {
  }

  @Input() displayText: string;
  @Input() permission: string = MenuPerms.read;
  @Input() component: number | Array<number> = 0;
  @Input() collapsible: boolean = false;
  @Input('defaultOpen') showList: boolean = true;

  iconHoverMode: BehaviorSubject<boolean> = new BehaviorSubject(false);
  iconHM = this.iconHoverMode.pipe(delay(0));

  doClick() {
    if (this.collapsible) {
      this.showList = !this.showList;
    }
  }

  @ContentChild(TemplateRef) menuTemplate: TemplateRef<any>;

  calcIf() {
    return SideMenuCommon.calcShow(this.component, this.permission, this.permissions);

  }

  ngOnInit() {
  }
}

/*
Creates a link in <aims-side-menu>
Must be inside <ng-template> tag
May or may not be inside of a <aims-side-menu-header> tag

Example Usage:
<aims-side-menu-link [route]="'./printShippingLabel'"
                      [component]="permissions.ComponentID.PrintShippingLabel"
                      [permission]="menuPerms.create"
                      [iconClass]="'fas fa-tag'">
  Print Shipping Label
  </aims-side-menu-link>

  [route]: MUST BE TEXT NOTE THE QUOTES AROUND IT, this is the route the link will go to
  [component]: optional, may be number or an array of numbers, link will not display if user does not have specified permission for at least one of the specified components
                if not specified, no permissions are required will display for all users
  [permission]: optional, use the MenuPerms constant above to specify, will default to "Read" permission, if no component specified, will have no effect
  [iconClass]: optional, enter text of class for font-awesome icon
 */

@Component({
  selector: 'aims-side-menu-link',
  styleUrls: ['./aimsSideMenu.component.css'],
  template: `
<li *ngIf="calcShow()">
  <ng-container>
      <a class="sidebarLink" routerLink={{route}} externalLink={{extHref}}><i *ngIf="iconClass !== undefined" [ngClass]="iconClass"></i> <ng-content *ngIf="!(iconHM|async)"></ng-content></a>
  </ng-container>

</li>
`
})
export class AimsSideMenuLinkComponent {
  constructor(private permissions: PermissionsService,
    @Inject(PLATFORM_ID) private platformId: Object) {

  }

  @Input() route: string = null;
  @Input() extHref: string = null;
  @Input() permission: string = MenuPerms.read;
  @Input() component: number | Array<number> = 0;
  @Input() iconClass: string;

  iconHoverMode: BehaviorSubject<boolean> = new BehaviorSubject(false);
  iconHM = this.iconHoverMode.pipe(delay(0));

  calcShow() {
    return SideMenuCommon.calcShow(this.component, this.permission, this.permissions);

  }

  ngOnInit() {
  }
}

/*
 This component creates the left hand nav and router outlet for the top level component of each module, note that you do NOT have to have a <router-outlet> tag as it
 inserts one for you
 You must insert an <ng-temple> inside the main element and use <aims-side-menu-header> and <aims-side-menu-link> components insdie the template
 (these components are documented below)
 You can insert normal html as well but it must be surrounded by <li> tags, should stay away from this as much as possible, see logistics.component.html for example, line 24

  IMPORT THE MenuPerms CONSTANT INTO YOUR COMPONENT IF YOU NEED TO SPECIFY PERMISSIONS (see below)

Example Usage:
<aims-side-menu [component]="permissions.ComponentID.Logistics">
  <ng-template>
    <aims-side-menu-link [component]="permissions.ComponentID.LogisticsDashboard" [iconClass]="'fas fa-tachometer-alt'"> Logistics Dashboard</aims-side-menu-link>
    <aims-side-menu-header [component]="[permissions.ComponentID.BuyersBuyQueue,
                             permissions.ComponentID.BuyersQuoteQueue]"
                           [displayText]="'Purchasing'">
      <aims-side-menu-link [route]="'./buyerBuy'"
                           [component]="permissions.ComponentID.BuyersBuyQueue"
                           [iconClass]="'fas fa-thumbs-up'">
        Buy Queue ({{(counts | async)[0]?.buyQueue}})
      </aims-side-menu-link>
    </aims-side-menu-header>
  </ng-template>
</aims-side-menu>

[component]: optional, if assigned the user must have read permission to the specified component

 */

@Component({
  selector: 'aims-side-menu',
  styleUrls: [],
  templateUrl: './aimsSideMenu.component.html'
})
export class AimsSideMenuComponent implements AfterViewInit {

  @Input() component: number | Array<number> = 0;
  @Input() permission: string = MenuPerms.read;
  @Input() allowHide: boolean = false;
  @Input() showByDefault: boolean = true;
  @Input() iconHoverMode: boolean = false;

  constructor(public permissions: PermissionsService,
    @Inject(PLATFORM_ID) private platformId: Object) {

  }

  showOnlyIcons: BehaviorSubject<boolean> = new BehaviorSubject(false);
  iconsOnly = this.showOnlyIcons.pipe(delay(0));
  showSidebar: boolean = true;
  showButton: boolean = false;
  collapseWidth: number = 768;

  prevWidth: number = (isPlatformBrowser(this.platformId)) ? window.innerWidth : 0;

  buttonClick() {
    this.showSidebar = !this.showSidebar;
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.checkSidebar();
  }

  @ContentChild(TemplateRef) menuTemplate: TemplateRef<any>;
  @ContentChildren(AimsSideMenuLinkComponent, { descendants: true }) links: QueryList<AimsSideMenuLinkComponent>;
  @ContentChildren(AimsSideMenuHeaderComponent, { descendants: true }) headers: QueryList<AimsSideMenuHeaderComponent>;

  calcIf() {
    return SideMenuCommon.calcShow(this.component, this.permission, this.permissions);
  }

  hoverOn() {
    if (this.iconHoverMode) {
      this.showOnlyIcons.next(false);
      this.links.forEach(link => link.iconHoverMode.next(false));
      this.headers.forEach(header => header.iconHoverMode.next(false));
    }
  }

  hoverOff() {
    if (this.iconHoverMode) {
      this.showOnlyIcons.next(true);
      this.links.forEach(link => link.iconHoverMode.next(true));
      this.headers.forEach(header => header.iconHoverMode.next(true));
    }
  }

  checkSidebar() {
    const newWidth: number = (isPlatformBrowser(this.platformId)) ? window.innerWidth : 0;
    if (newWidth !== this.prevWidth) {
      if (newWidth < this.collapseWidth) {
        this.showSidebar = false;
        this.showButton = true && this.calcIf();
      }
      else {
        this.showSidebar = true && (this.showByDefault || this.showSidebar) && this.calcIf();
        this.showButton = false || this.allowHide;
      }
      this.prevWidth = newWidth;
    }
  }

  ngAfterViewInit() {
    //Required settings for iconHoverMode
    //Overrides the showButton and showSidebar settings
    if (this.iconHoverMode) {
      this.showButton = false;
      this.showSidebar = true;
      this.allowHide = false;
      this.showByDefault = true;
      this.showOnlyIcons.next(true);
      //Have to wait for the query to resolve using subscribe
      this.links.changes.pipe(take(1)).subscribe(c => this.links.forEach(link => link.iconHoverMode.next(true)));
      this.headers.changes.pipe(take(1)).subscribe(c => this.headers.forEach(header => header.iconHoverMode.next(true)));
    }
    else {
      this.showButton = this.allowHide;
      this.showSidebar = this.showByDefault;
    }
    this.checkSidebar();
  }
}


// Import this into your component to use
// Example: import { MenuPerms } from '../../components/aims-side-menu/aimsSideMenu.component';
// Then set it to a variable to utilize
// Example: menuPerms = MenuPerms;
export const MenuPerms = {
  create: 'Create',
  read: 'Read',
  update: 'Update',
  delete: 'Delete'
}



class SideMenuCommon {
  public static calcShow(component: number | Array<number>, permission: string, permissions: PermissionsService) {
    let show = false;
    if (permissions.permissions.value) {
      if (component !== 0) {
        const arrComp: Array<number> = (typeof component == 'number') ? [<number>component] : <Array<number>>component;
        switch (permission) {
          case MenuPerms.create: {
            show = false;
            for (let c of arrComp) {
              if (permissions.canCreate(c)) {
                show = true;
              }
            }
            break;
          }
          case MenuPerms.read: {
            show = false;
            for (let c of arrComp) {
              if (permissions.canRead(c)) {
                show = true;
              }
            }
            break;
          }
          case MenuPerms.update: {
            show = false;
            for (let c of arrComp) {
              if (permissions.canUpdate(c)) {
                show = true;
              }
            }
            break;
          }
          case MenuPerms.delete: {
            show = false;
            for (let c of arrComp) {
              if (permissions.canDelete(c)) {
                show = true;
              }
            }
            break;
          }
        }
      }
      else {
        show = true;
      }
    }

    return show;
  }
}

@Directive({
  selector: '[externalLink]'
})
export class ExternalLinkDirective {

  @Input() externalLink: string;

  constructor(
    // Inject RouterLinkWithHref
    @Optional() private link: RouterLinkWithHref,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {

    if (!link) {
      return;
    }

    // Save original onClick method
    const onClick = link.onClick;

    // Replace method with your own (I know this is not good idea)
    link.onClick = (...args: any[]) => {
      if (this.externalLink) {
        // Process external url
        if (isPlatformBrowser(this.platformId)) {
          window.location.href = this.externalLink;
        }
        return false;
      } else {
        // Process internal url by calling routerLink original method
        return onClick.apply(link, args);
      }
    }

  }

}
